<template lang="pug">
  .width-100.file-uploader(:style='{"max-width": maxWidth}' :class='{modal: forModal}')
    .header.row.space-between.middle.m-bottom-1(v-if='header')
      h1.title-medium
        | {{ navigationLanguage ? $t(`krea.kreaFileUploader.${type}.header`) : innerI18n.t(`krea.kreaFileUploader.${type}.header`) }}
      button(@click='closeUploader')
        simple-svg(:filepath='crossIcon' width='20px' height='20px')
    .container(:class='{modal: forModal}')
      .width-100(v-if='type !== "video"')
        .width-100(@drop.prevent='dropFile' @dragenter.prevent @dragover.prevent v-show='!fileUploading')
          label.input-area.column.middle.center.m-bottom-1
            input.input-hidden(type='file' @change='selectFile' ref='kreaFileInput' :accept='inputAccept')
            simple-svg.gray-icon.gray-stroke-icon.m-bottom-1(:filepath='icons[type]' width='30px' height='30px')
            h2.text-center.text-big.blue.m-bottom-05
              | {{ navigationLanguage ? $t(`krea.kreaFileUploader.${type}.selectFile`) : innerI18n.t(`krea.kreaFileUploader.${type}.selectFile`) }}
            h2.text-center.text-normal.dark-gray
              | {{ navigationLanguage ? $t(`krea.kreaFileUploader.${type}.dropFile`) : innerI18n.t(`krea.kreaFileUploader.${type}.dropFile`) }}
        .input-area.loading.column.middle.center.m-bottom-1(v-show='fileUploading')
          .row.middle.center.relative.spinner-container
            .spinner.m-bottom-05.row.middle.center
            h2.spinner-text.text-small {{ fileProgress + '%' }}
          h2.text-center.text-big.blue.m-bottom-1
            | {{ navigationLanguage ? $t(`krea.kreaFileUploader.uploadingFile`) : innerI18n.t(`krea.kreaFileUploader.uploadingFile`) }}
          button.small-button.secondary-button(@click='cancelUpload')
            | {{ navigationLanguage ? $t(`krea.kreaFileUploader.cancel`) : innerI18n.t(`krea.kreaFileUploader.cancel`) }}
        .row.m-top-1.m-bottom-1(v-if='extensionError')
            simple-svg.error-icon.red-icon.m4-right-2.red-stroke-icon(:filepath='errorIcon' :width='"16px"' :height='"16px"')
            span.text-small.red
              | {{ navigationLanguage ? $t("krea.kreaFileUploader.extensionError") : innerI18n.t("krea.kreaFileUploader.extensionError") }}
      .link(v-if='link')
        h2.text-normal(v-if='type !== "video"')
          | {{ navigationLanguage ? $t(`krea.kreaFileUploader.${linkType || type}.url`) : innerI18n.t(`krea.kreaFileUploader.${linkType || type}.url`) }}
        .link-input.row.middle.center
          input.input-base.input-border(
            v-model='fileLinkText'
            :disabled='fileUploading'
            :placeholder='navigationLanguage ? $t("krea.kreaFileUploader.insertUrl") : innerI18n.t("krea.kreaFileUploader.insertUrl")'
          )
          button.m4-top-2.m-left-05.primary-button.submit(:disabled='fileUploading' @click='submitLink')
            | {{ navigationLanguage ? $t(`krea.kreaFileUploader.urlSubmit`) : innerI18n.t(`krea.kreaFileUploader.urlSubmit`)}}
        .row.m-top-1(v-if='showError')
          simple-svg.error-icon.red-icon.m4-right-2.red-stroke-icon(:filepath='errorIcon' :width='"16px"' :height='"16px"')
          span.text-small.red
            | {{ navigationLanguage ? $t("krea.kreaFileUploader.linkError") : innerI18n.t("krea.kreaFileUploader.linkError") }}
</template>

<script>
import errorIcon from '@/assets/ic_stop.svg'
import crossIcon from '@/assets/ic_cross.svg'
import image from '@/assets/ic_image.svg'
import pdf from '@/assets/krea/ic_pdf.svg'
import video from '@/assets/ic_play.svg'
import { mapActions, mapState } from 'vuex'
import { getBase64, getExtension } from '@/utils/file'
import Axios from 'axios'
import { kreaFileAcceptedFiles } from '@/constants/kreaFileUploader'
import { isError } from '@/utils/api'
import { uploadFile, uploadKreaContentFile, uploadKreaResourceFile } from '../../../services/FileUploadService'
import { routes } from '../../../router'
import { innerI18n } from '../../../main'

export default {
  props: {
    type: { type: String },
    link: { type: Boolean },
    linkType: { type: String },
    header: { type: Boolean },
    forModal: { type: Boolean, default: false },
    maxWidth: { type: String, default: 'none' },
    updateFile: { type: Function, required: true },
    updateLink: { type: Function },
    navigationLanguage: { type: Boolean, default: true }
  },
  data() {
    return {
      crossIcon,
      errorIcon,
      icons: {
        image,
        pdf,
        video
      },
      innerI18n,
      fileLinkText: '',
      showError: false,
      extensionError: false,
      file: null,
      fileUploading: false,
      fileProgress: 0,
      fileController: Axios.CancelToken.source()
    }
  },
  computed: {
    ...mapState({
      userInfo: state => state.userInfo.userInfo,
      kreaResource: state => state.kreaResource.resource,
      kreaContent: state => state.kreaEducationalContent.content
    }),
    inputAccept() {
      if (this.type === 'image') return 'image/png, image/jpeg, image/jpg'
      else if (this.type === 'pdf') return '.pdf'
      else return 'video/*'
    },
    isKreaResourceView() {
      return Boolean(this.$route.path.includes('/krea/resource'))
    },
    isKreaContentView() {
      return Boolean(this.$route.name === routes.kreaEducationalContent)
    }
  },
  methods: {
    ...mapActions([
      'showElement'
    ]),
    selectFile(e) {
      this.uploadFile(e.target.files[0])
    },
    dropFile(e) {
      this.uploadFile(e.dataTransfer.files[0])
    },
    async upload({ content, extension, fileName }) {
      const { schoolGroupId, schoolGroupName } = this.userInfo
      if (this.isKreaResourceView) {
        const contentId = this.kreaResource.educationalProgramId
        const response = await uploadKreaResourceFile({
          file: content,
          fileName: fileName.replace(/\.[^/.]+$/, ''),
          contentId,
          extension,
          schoolGroupId,
          schoolGroupName
        }, {
          onUploadProgress: this.updateProgress,
          cancelToken: this.fileController.token
        })
        return response
      } else if (this.isKreaContentView) {
        const contentId = this.kreaContent.id
        const response = await uploadKreaContentFile({
          file: content,
          fileName: fileName.replace(/\.[^/.]+$/, ''),
          contentId,
          extension,
          schoolGroupId,
          schoolGroupName
        }, {
          onUploadProgress: this.updateProgress,
          cancelToken: this.fileController.token
        })
        return response
      } else {
        const response = await uploadFile({ content, extension }, {
          onUploadProgress: this.updateProgress,
          cancelToken: this.fileController.token
        })
        return response
      }
    },
    async uploadFile(file) {
      this.extensionError = false
      const fileName = file.name
      const content = await getBase64(file)
      const extension = getExtension(fileName)
      if (!kreaFileAcceptedFiles[this.type].test(extension)) {
        this.extensionError = true
        return
      }
      this.fileUploading = true
      const response = await this.upload({ content, extension, fileName })
      const success = !isError(response.status)
      if (success) this.updateFile(response.data)
      this.$refs.kreaFileInput.value = null
      this.fileUploading = false
      this.fileProgress = 0
    },
    cancelUpload() {
      this.fileProgress = 0
      this.fileUploading = false
      this.fileController.cancel()
    },
    updateProgress(event) {
      const totalLength = event.lengthComputable
        ? event.total
        : event.target.getResponseHeader('content-length') || event.target.getResponseHeader('x-decompressed-content-length')
      if (totalLength) {
        this.fileProgress = Math.round((event.loaded * 100) / totalLength)
      }
    },
    submitLink() {
      this.showError = false
      if (!this.fileLinkText) {
        this.showError = true
        return
      }
      this.updateLink(this.fileLinkText)
      this.$emit('close')
    },
    closeUploader() {
      this.cancelUpload()
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';

.file-uploader{
  background-color: $white;
  border-radius: 10px;
  overflow: hidden;
  min-width: 380px;
  box-shadow: 0px 5px 4px 0px $shadow-gray1;
  &.modal{
    border-radius: initial;
    box-shadow: none;
    -webkit-box-shadow: none;
    -moz-box-shadow: none;
  }
}

.container{
  background-color: $white;
  padding: 15px;
  margin: 0 auto;
  border-radius: 10px;
  &.modal{
    padding: 0;
    border-radius: initial;
  }
}

.header{
  background-color: $white;
  padding: 10px 15px;
  box-shadow: 1px 12px 10px -4px $shadow-gray;
  -webkit-box-shadow: 1px 12px 10px -4px $shadow-gray;
  -moz-box-shadow: 1px 12px 10px -4px $shadow-gray;
}

.input-hidden{
  display: none;
}

.input-area{
  cursor: pointer;
  height: 140px;
  border: 1px dashed $gray;
  border-radius: 10px;
  &.loading{
    cursor: default;
  }
}

.spinner-text{
  position: absolute;
  left: 50%;
  top: 30%;
  transform: translateX(-50%);
}

.small-button{
  height: 30px;
}

.spinner{
  width: 50px;
  height: 50px;
  padding: 5px;
  border: 4px solid transparent;
  border-top: 4px solid $blue;
  border-left: 4px solid $blue;
  border-radius: 50%;
  animation: loading 1s linear infinite;
}

@keyframes loading {
  from{
    transform: rotate(0);
  }
  to{
    transform: rotate(360deg);
  }
}
</style>
